import React, { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Trash2, CheckCircle, Upload } from 'lucide-react';
import { purchaseApi } from '../api/purchase';
import { coaApi } from '../api/coa';
import { useToast } from '../hooks/useToast';

interface Props { grnId: number; onBack: () => void; onConfirmed: () => void; }
const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', purple:'badge-purple', red:'badge-red' };

const GrnDetail: React.FC<Props> = ({ grnId, onBack, onConfirmed }) => {
  const { showToast } = useToast();
  const [grn, setGrn]                   = useState<any>(null);
  const [accounts, setAccounts]         = useState<any[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showLCForm, setShowLCForm]     = useState(false);
  const [showImport, setShowImport]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [importRows, setImportRows]     = useState<any[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [lcForm, setLcForm] = useState({ cost_type:'Transport', description:'', amount:'', credit_account_id:'', allocation_method:'by_value' });

  const load = () => {
    setLoading(true);
    coaApi.list().then(r => setAccounts(r.data.data.filter((a: any) => a.type_name === 'Liability' || a.type_name === 'Expense')));
    fetch(`https://merchanerp.com/api/grns/${grnId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
    }).then(r => r.json()).then(d => { setGrn(d.data); setLoading(false); });
  };

  useEffect(() => { load(); }, [grnId]);

  const downloadTemplate = () => {
    const csv = [
      'sku,product_name,category,unit_symbol,branch_name,location_name,qty,unit_cost,selling_price,batch_no,expiry_date',
      'sa89,Samsung A13,Phones,pcs,Main Branch,Main Warehouse,100,25.50,30.00,,',
      'PROD-002,iPhone 14,Phones,pcs,Main Branch,Main Warehouse,50,800.00,950.00,BATCH-001,2027-12-31'
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'grn_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const res = await purchaseApi.importPreview(file);
      setImportRows(res.data.data.rows);
      setImportErrors(res.data.data.errors);
      showToast(`${res.data.data.total} rows ready.${res.data.data.errors.length > 0 ? ' '+res.data.data.errors.length+' warnings.' : ''}`, res.data.data.errors.length > 0 ? 'warning' : 'success');
    } catch (err: any) { showToast(err.response?.data?.message || 'Upload failed', 'error'); }
    finally { setImportLoading(false); }
  };

  const handleImportSave = async () => {
    if (importRows.length === 0) { showToast('No valid rows to import', 'error'); return; }
    const validRows = importRows.filter((r: any) => r.location_id);
    if (validRows.length === 0) { showToast('No rows with valid locations', 'error'); return; }
    setSaving(true);
    try {
      const res = await purchaseApi.importSave(grnId, { rows: validRows });
      showToast(res.data.message);
      setShowImport(false);
      setImportRows([]);
      setImportErrors([]);
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleAddLC = async () => {
    if (!lcForm.amount || !lcForm.credit_account_id) { showToast('Amount and account required', 'error'); return; }
    setSaving(true);
    try {
      await purchaseApi.addLandedCost(grnId, { ...lcForm, amount: Number(lcForm.amount), credit_account_id: Number(lcForm.credit_account_id) });
      showToast('Landed cost added!');
      setShowLCForm(false);
      setLcForm({ cost_type:'Transport', description:'', amount:'', credit_account_id:'', allocation_method:'by_value' });
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteLC = async (costId: number) => {
    try { await purchaseApi.deleteLandedCost(grnId, costId); showToast('Removed'); load(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this GRN? This will post to accounting and update inventory.')) return;
    try {
      const res = await purchaseApi.confirmGrn(grnId);
      showToast(res.data.message); onConfirmed();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!grn) return <div className="empty-state"><p>GRN not found.</p></div>;

  const isDraft     = grn.status === 'Draft';
  const hasItems    = grn.items?.length > 0;
  const itemsTotal  = grn.items?.reduce((s: number, i: any) => s + (i.qty_received * i.unit_cost), 0) || 0;
  const landedTotal = grn.landed_costs?.reduce((s: number, l: any) => s + Number(l.amount), 0) || 0;

  return (
    <>
      <div className="page-header">
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-secondary btn-sm" onClick={onBack}><ArrowLeft size={13} /> Back</button>
          <h1 className="page-title">{grn.grn_no}</h1>
          <span className={`badge ${colorMap[grn.status_color]||'badge-gray'}`}>{grn.status}</span>
        </div>
        {isDraft && (
          <div style={{ display:'flex', gap:8 }}>
            {!hasItems && <button className="btn btn-secondary" onClick={() => { setShowImport(!showImport); setShowLCForm(false); }}><Upload size={13} /> Import CSV</button>}
            {hasItems && <button className="btn btn-secondary" onClick={() => { setShowLCForm(!showLCForm); setShowImport(false); }}><Plus size={13} /> Landed Cost</button>}
            {hasItems && <button className="btn btn-success" onClick={handleConfirm}><CheckCircle size={13} /> Confirm GRN</button>}
          </div>
        )}
      </div>

      <div className="form-grid-3" style={{ marginBottom:16 }}>
        <div className="kpi-card"><div className="kpi-label">Supplier</div><div style={{ fontSize:14, fontWeight:600 }}>{grn.supplier_name}</div></div>
        <div className="kpi-card"><div className="kpi-label">Items Total</div><div className="kpi-value" style={{ fontSize:18, color:'var(--blue)' }}>{itemsTotal.toFixed(2)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Landed Costs</div><div className="kpi-value" style={{ fontSize:18, color:'var(--amber)' }}>{landedTotal.toFixed(2)}</div></div>
      </div>

      {showImport && isDraft && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <span>Import Items from CSV</span>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowImport(false); setImportRows([]); setImportErrors([]); }}>Cancel</button>
          </div>
          <div className="card-body">
            <div className="alert alert-info" style={{ marginBottom:12 }}>
              <strong>Required:</strong> sku, qty, unit_cost, product_name, category, unit_symbol, branch_name, location_name &nbsp;
              <strong>Optional:</strong> selling_price, batch_no, expiry_date
              <button className="btn btn-secondary btn-sm" style={{ marginLeft:12 }} onClick={downloadTemplate}>Download Template</button>
            </div>
            <div className="form-group" style={{ marginBottom:14 }}>
              <label className="form-label">Upload CSV File</label>
              <input type="file" accept=".csv,.txt" className="form-control" onChange={handleFileUpload} disabled={importLoading} />
            </div>
            {importErrors.length > 0 && (
              <div className="alert alert-warning" style={{ marginBottom:12 }}>
                <strong>{importErrors.length} warnings:</strong>
                <ul style={{ margin:'8px 0 0 16px', fontSize:11 }}>
                  {importErrors.map((e,i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            {importRows.length > 0 && (
              <>
                <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:'var(--tx2)' }}>
                  Preview — {importRows.length} rows — Total: {importRows.reduce((s,r) => s + r.line_total, 0).toFixed(2)}
                </div>
                <div style={{ maxHeight:300, overflowY:'auto', border:'1px solid var(--bdr)', borderRadius:7, marginBottom:14 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'var(--sur2)', position:'sticky', top:0 }}>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Sell Price</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                    </tr></thead>
                    <tbody>
                      {importRows.map((r,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                          <td style={{ padding:'8px 12px', fontFamily:'monospace', color:'var(--blue)', fontWeight:700, fontSize:12 }}>{r.sku}</td>
                          <td style={{ padding:'8px 12px', fontSize:12 }}>{r.product_name}</td>
                          <td style={{ padding:'8px 12px', fontSize:12, color: r.location_id ? 'var(--tx2)' : 'var(--red)' }}>{r.location_name || 'No location'}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{r.qty_received}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{Number(r.unit_cost).toFixed(2)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12, color:'var(--green)' }}>{Number(r.selling_price).toFixed(2)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontWeight:600, fontSize:12 }}>{Number(r.line_total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-primary" onClick={handleImportSave} disabled={saving}>{saving ? 'Importing...' : `Import ${importRows.length} Items`}</button>
              </>
            )}
          </div>
        </div>
      )}

      {showLCForm && isDraft && hasItems && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Add Landed Cost</span><button className="btn btn-secondary btn-sm" onClick={() => setShowLCForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Cost Type</label>
                <select className="form-control" value={lcForm.cost_type} onChange={e => setLcForm(f=>({...f,cost_type:e.target.value}))}>
                  <option>Transport</option><option>Customs</option><option>Handling</option><option>Insurance</option><option>Other</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Amount *</label>
                <input className="form-control mono" type="number" value={lcForm.amount} onChange={e => setLcForm(f=>({...f,amount:e.target.value}))} placeholder="0.00" />
              </div>
              <div className="form-group"><label className="form-label">Credit Account *</label>
                <select className="form-control" value={lcForm.credit_account_id} onChange={e => setLcForm(f=>({...f,credit_account_id:e.target.value}))}>
                  <option value="">Select account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Description</label>
                <input className="form-control" value={lcForm.description} onChange={e => setLcForm(f=>({...f,description:e.target.value}))} />
              </div>
              <div className="form-group"><label className="form-label">Allocation Method</label>
                <select className="form-control" value={lcForm.allocation_method} onChange={e => setLcForm(f=>({...f,allocation_method:e.target.value}))}>
                  <option value="by_value">By Value</option><option value="by_qty">By Quantity</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleAddLC} disabled={saving}>{saving ? 'Saving...' : 'Add Cost'}</button>
          </div>
        </div>
      )}

      {hasItems && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Items Received</span><span className="record-count">{grn.items?.length} items</span></div>
          <table>
            <thead><tr>
              <th>SKU</th><th>Product</th><th>Location</th>
              <th style={{textAlign:'right'}}>Qty</th>
              <th style={{textAlign:'right'}}>Unit Cost</th>
              <th style={{textAlign:'right'}}>Total</th>
            </tr></thead>
            <tbody>
              {grn.items?.map((item: any) => (
                <tr key={item.id}>
                  <td className="td-code">{item.sku}</td>
                  <td>{item.product_name}</td>
                  <td style={{color:'var(--tx3)'}}>{item.location_name}</td>
                  <td className="td-num">{item.qty_received}</td>
                  <td className="td-num">{Number(item.unit_cost).toFixed(2)}</td>
                  <td className="td-num td-green">{(item.qty_received * item.unit_cost).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      

      {grn.landed_costs?.length > 0 && (
        <div className="card">
          <div className="card-header"><span>Landed Costs</span><span className="record-count">{grn.landed_costs.length} costs</span></div>
          <table>
            <thead><tr>
              <th>Type</th><th>Description</th><th>Account</th><th>Allocation</th>
              <th style={{textAlign:'right'}}>Amount</th>
              {isDraft && <th></th>}
            </tr></thead>
            <tbody>
              {grn.landed_costs.map((lc: any) => (
                <tr key={lc.id}>
                  <td><span className="badge badge-amber">{lc.cost_type}</span></td>
                  <td style={{color:'var(--tx3)'}}>{lc.description || '—'}</td>
                  <td style={{color:'var(--tx3)'}}>{lc.account_code} {lc.account_name}</td>
                  <td><span className="badge badge-blue">{lc.allocation_method}</span></td>
                  <td className="td-num">{Number(lc.amount).toFixed(2)}</td>
                  {isDraft && (
                    <td>
                      <button onClick={() => handleDeleteLC(lc.id)} style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer'}}>
                        <Trash2 size={13}/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default GrnDetail;


